import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    // Captured before the variant picker's own change listener, which dispatches the product select event
    target?.addEventListener('change', this.#handleOptionChange, { signal, capture: true });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.closest('.product-information__media')) this.#setupAdaptiveHeight(signal);
  }

  /**
   * MELLY: on the product page the slide area takes the height of the active slide, so landscape room and
   * frame images are not letterboxed inside the height of a portrait artwork. Each slide gets its image's
   * own proportions (the slide's preview ratio is lost to a duplicate style attribute in slideshow-slide).
   * The CSS in melly-product-styles applies this below 750px only, once data-melly-adaptive is set. It also
   * caps each image to the visible screen (--melly-gallery-fit) and frames the canvas/frame mockups.
   * @param {AbortSignal} signal
   */
  #setupAdaptiveHeight(signal) {
    const slideshow = this.refs.slideshow;
    const slides = slideshow?.querySelector(':scope > slideshow-container > slideshow-slides');
    if (!slideshow || !(slides instanceof HTMLElement)) return;

    for (const slide of slides.children) {
      const image = slide.querySelector('img');
      const width = Number(image?.getAttribute('width'));
      const height = Number(image?.getAttribute('height'));
      if (slide instanceof HTMLElement && width > 0 && height > 0) {
        slide.style.setProperty('--melly-slide-ratio', `${width} / ${height}`);
      }
    }

    // The slideshow may connect after this gallery (e.g. when a variant change replaces the gallery), so
    // read the slides from the DOM and fall back to its initial slide until it has a current index.
    // Height the slide media may take so the whole image and the thumbnail strip fit on screen at the top of
    // the page: the small viewport height (browser bars shown) minus the gallery's offset (announcement bar
    // and header) and the thumbnails. Never below 240px.
    const viewportUnit = CSS.supports('height', '100svh') ? '100svh' : '100vh';
    const setFit = () => {
      const offset = Math.round(this.getBoundingClientRect().top + window.scrollY);
      const controls = this.querySelector('slideshow-controls');
      const strip = controls instanceof HTMLElement ? controls.offsetHeight : 0;
      slides.style.setProperty('--melly-gallery-fit', `max(240px, calc(${viewportUnit} - ${offset + strip + 16}px))`);
    };

    /** @param {Element} [shown] - The active slide; defaults to the slideshow's current (or initial) slide. */
    const update = (shown) => {
      setFit();
      const current = Number.isInteger(slideshow.current) ? slideshow.current : Number(slideshow.getAttribute('initial-slide')) || 0;
      const active = shown ?? slideshow.slides?.[current] ?? slides.children[current];
      if (active instanceof HTMLElement && active.offsetHeight > 0) {
        // On the container, which clips the slides (melly-product-styles)
        slides.parentElement?.style.setProperty('--melly-active-slide-height', `${active.offsetHeight}px`);
      }
    };

    this.addEventListener(
      SlideshowSelectEvent.eventName,
      (event) => {
        // Every navigation (thumbnail, arrow, swipe, drag) reports its slide; a drag reports it before the
        // slideshow updates its current index, so the event's slide is the one to follow
        if (event.target === slideshow) update(event.detail?.slide);
      },
      { signal }
    );

    // Once a swipe settles, the slide actually in view is the source of truth: if the slideshow's index (and
    // so the selected thumbnail) disagrees, select that slide without moving it. Phones only.
    const settle = () => {
      if (mediaQueryLarge.matches || slideshow.hasAttribute('dragging')) return;
      const list = slideshow.slides ?? [];
      const center = slides.getBoundingClientRect().left + slides.clientWidth / 2;
      let shown = list[0];
      let distance = Infinity;
      for (const slide of list) {
        const rect = slide.getBoundingClientRect();
        const offset = Math.abs(rect.left + rect.width / 2 - center);
        if (offset < distance) [shown, distance] = [slide, offset];
      }
      if (!shown) return;
      const index = list.indexOf(shown);
      if (index !== slideshow.current) slideshow.select(index, undefined, { animate: false });
      else update(shown);
    };

    if ('onscrollend' in window) {
      slides.addEventListener('scrollend', settle, { signal });
    } else {
      /** @type {number | undefined} */
      let timer;
      slides.addEventListener(
        'scroll',
        () => {
          clearTimeout(timer);
          timer = setTimeout(settle, 120);
        },
        { signal, passive: true }
      );
      signal.addEventListener('abort', () => clearTimeout(timer));
    }

    // Width changes (rotation, resize) change every slide's height
    const observer = new ResizeObserver(update);
    observer.observe(slideshow);
    signal.addEventListener('abort', () => observer.disconnect());

    this.dataset.mellyAdaptive = '';
    update();
    queueMicrotask(update);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Read synchronously: the change that caused this product select set it a moment ago
    const revealFrame = this.#frameChanged;
    this.#frameChanged = false;

    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
        if (revealFrame && newMediaGallery instanceof MediaGallery) newMediaGallery.revealActiveMedia();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /** Whether the latest option change was the customer picking another frame finish on a phone. */
  #frameChanged = false;

  /**
   * MELLY: notes a customer's own change of frame finish on a phone (the frame option is the product's
   * only swatch option, linked to the colour-pattern metafield), so the gallery that the variant change
   * renders can be brought into view. Radios fire no change when the selected finish is picked again;
   * sizes, page load, URL variants, thumbnails and swipes never set it.
   * @param {Event} event - The change event, captured on the section.
   */
  #handleOptionChange = (event) => {
    const { target } = event;
    this.#frameChanged =
      event.isTrusted &&
      !mediaQueryLarge.matches &&
      target instanceof HTMLInputElement &&
      target.checked &&
      Boolean(target.closest('variant-picker fieldset.variant-option--swatches')) &&
      !target.closest('product-card, quick-add-dialog');
  };

  /**
   * Scrolls the page to the gallery once its active image is ready, below the sticky header. Used after the
   * customer picks another frame finish on a phone. Smooth unless reduced motion is preferred; any user
   * scroll interrupts it.
   */
  async revealActiveMedia() {
    const slideshow = this.refs.slideshow;
    const slides = slideshow?.slides;
    const index = Number.isInteger(slideshow?.current) ? slideshow?.current : Number(slideshow?.getAttribute('initial-slide')) || 0;
    const image = slides?.[index ?? 0]?.querySelector('img') ?? this.querySelector('img');

    if (image instanceof HTMLImageElement) {
      image.loading = 'eager';
      try {
        await image.decode();
      } catch {
        // A failed or broken image still has its slot reserved; scroll anyway
      }
    }

    if (!this.isConnected || mediaQueryLarge.matches) return;

    const header = document.querySelector('#header-component');
    const headerHeight = header?.hasAttribute('sticky') ? header.getBoundingClientRect().height : 0;
    const top = this.getBoundingClientRect().top + window.scrollY - headerHeight - 8;

    window.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
